/**
 * useCardQueries — 카탈로그 조회(검색·세트 목록) react-query 훅.
 *
 * 카탈로그는 수집서버 적재분을 읽기만 하는 참조 데이터라 사용자가 바꿀 수 없다. 그래서
 * 알림·컬렉션(30초)보다 훨씬 길게 캐시하고 포커스 재조회도 하지 않는다.
 *
 * ⚠ 페이징이 `usePagedListQuery` 를 쓰지 않는다 — 그 훅은 `PageResponse`(1-based page +
 * totalCount)를 전제하는데 카드 검색은 **0-based 이고 전체 건수가 없다**(BE 가 COUNT 를
 * 피하려고 size+1 을 읽어 hasNext 만 판정한다). 1-based 훅을 그대로 쓰면 첫 페이지가
 * page=1 로 나가 0페이지를 통째로 건너뛴다.
 */
import { useEffect, useMemo } from "react";
import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  searchCards,
  fetchCardsets,
  fetchCardsetDetail,
  fetchCardDetail,
  fetchCardPriceHistory,
  fetchTrendingCards,
  type CardDetail,
  type CardItem,
  type CardSearchResponse,
  type CardsetDetail,
  type CardsetSummary,
  type CardSortKey,
  type HistoryPeriod,
  type PriceHistory,
  type TrendChipKey,
} from "../api/card";
import { detailKey, listKey } from "../lib/queryKeys";

/** 카탈로그 캐시 신선도 — 적재는 하루 단위라 5분이면 충분히 짧다. */
const CATALOG_STALE_TIME = 5 * 60_000;

export const CARD_SEARCH_PAGE_SIZE = 20;

export interface CardSearchOptions {
  /** 이미 디바운스된 검색어. 세트 지정이 없으면 이게 비었을 때 호출하지 않는다. */
  keyword?: string;
  /** 카드 에디션(SC-009) — 검색 결과와 시세 기준이 되는 언어판 */
  language?: string;
  /** 세트 안에서만 찾을 때 (SC-006 세트 상세) */
  cardsetSerialNo?: number;
  rarities?: string[];
  minPrice?: number;
  maxPrice?: number;
  sort?: CardSortKey;
}

/**
 * 카드 검색 — 무한 스크롤.
 *
 * **검색어도 세트 지정도 없으면 호출하지 않는다.** 카탈로그 전량(5.7만장)을 조건 없이 훑는
 * 화면은 없다 — 검색 화면의 무검색어 상태는 세트 브라우즈가 담당하고, 세트 상세는 항상
 * `cardsetSerialNo` 로 범위가 잡혀 있다.
 *
 * ⚠ 필터·정렬은 전부 **서버**로 넘긴다. 페이지 단위로 받아 앱에서 거르면 "결과 20건"이
 * 필터 후 3건이 되고, 다음 페이지를 받을 때마다 숫자가 늘어난다.
 */
export function useCardSearchQuery(options: CardSearchOptions) {
  const trimmed = (options.keyword ?? "").trim();
  const params = {
    keyword: trimmed || undefined,
    language: options.language,
    cardsetSerialNo: options.cardsetSerialNo,
    rarities: options.rarities,
    minPrice: options.minPrice,
    maxPrice: options.maxPrice,
    sort: options.sort,
  };

  return useInfiniteQuery<CardSearchResponse, Error>({
    queryKey: listKey("cards", "search", params),
    queryFn: ({ pageParam }) =>
      searchCards({ ...params, page: pageParam as number, size: CARD_SEARCH_PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.page + 1 : undefined),
    enabled: trimmed.length > 0 || options.cardsetSerialNo !== undefined,
    staleTime: CATALOG_STALE_TIME,
    // 에디션·필터를 바꾸거나 글자를 이어 칠 때 결과가 빈 화면으로 깜빡이지 않게 한다.
    placeholderData: keepPreviousData,
  });
}

/** 무한스크롤 페이지들을 단일 배열로 펼친다. */
export function flattenCards(data: { pages: CardSearchResponse[] } | undefined): CardItem[] {
  return data ? data.pages.flatMap((page) => page.items) : [];
}

/** 세트 목록 — 검색어가 없을 때의 브라우즈 진입로. 언어별로 캐시된다. */
export function useCardsetsQuery(language: string) {
  return useQuery<CardsetSummary[]>({
    queryKey: listKey("cardsets", "browse", { language }),
    queryFn: () => fetchCardsets(language),
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * 홈 §2 트렌딩 (FN-048). 랭킹은 시세 데이터라 자주 안 바뀐다 — 카탈로그와 같은 신선도.
 * 빈 목록이 정상 경로다(가격 미적재 환경) — 화면이 빈 상태를 그린다.
 */
export function useTrendingQuery(
  chip: TrendChipKey,
  language: string,
  limit?: number,
  opts?: { enabled?: boolean },
) {
  return useQuery<CardItem[]>({
    queryKey: listKey("cards", "trending", { chip, language, limit }),
    queryFn: () => fetchTrendingCards({ chip, language, limit }),
    enabled: opts?.enabled ?? true,
    staleTime: CATALOG_STALE_TIME,
    // keepPreviousData 를 쓰지 않는다 — 칩을 바꿀 때 이전 칩 목록이 그대로 남아
    // "바뀌었는지" 알 수 없었다(2026-09-07 제보). 새 칩은 스켈레톤이 뜨고,
    // 이미 받아 둔 칩은 캐시라 즉시 그려진다.
  });
}

/** 세트 상세 — 헤더 정보 + 그 세트에 실재하는 희귀도(필터 옵션). */
export function useCardsetDetailQuery(cardsetSerialNo: number | null) {
  return useQuery<CardsetDetail>({
    queryKey: detailKey("cardsets", cardsetSerialNo ?? 0),
    queryFn: () => fetchCardsetDetail(cardsetSerialNo as number),
    enabled: cardsetSerialNo !== null,
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * 세트 전량 로딩 페이지 크기. BE `CardService.MAX_SIZE` 가 100 이라 더 크게 보내도 잘린다.
 */
const CARDSET_ALL_PAGE_SIZE = 100;

/** 세트 평균가 (SC-006 헤더). */
export interface CardsetPriceSummary {
  /** 시세가 있는 카드의 평균가. 아직 한 장도 못 봤으면 null. */
  average: number | null;
  /** 평균을 낸 통화 — 첫 시세 카드의 것. 서버가 KRW 로 굳혀 내려보낸다. */
  currency: string | null;
  /** 평균의 **분모** — 시세가 있는 카드 수. 화면이 "N장 기준"으로 밝힌다. */
  pricedCount: number;
  /** 세트 전량을 다 받았는가. 받는 동안 평균은 계속 움직인다. */
  isComplete: boolean;
}

/**
 * 세트 평균가 — 그 세트 카드를 **전량** 받아 앱에서 낸다.
 *
 * <p>BE 에 세트 단위 집계가 없어서다. 페이지당 100장이라 세트 넷 중 셋은 요청 한 번으로
 * 끝나고(실측: 1,787개 중 1,300개가 50장 이하), 가장 큰 세트(782장)가 8번이다.
 *
 * <p>⚠ **목록 쿼리와 따로 둔다.** 목록에는 희귀도·가격대 필터가 걸려 있어 그걸로 평균을
 * 내면 필터를 만질 때마다 세트 평균이 바뀐다. 평균은 필터와 무관한 세트의 성질이다.
 * 그 대가로 첫 100장이 중복 수신되지만, 필터가 걸린 목록과 키를 섞는 것보다 낫다.
 *
 * <p>⚠ 시세가 없는 카드는 분모에서 뺀다 — 0원으로 세면 평균이 내려앉는다. 세트별 시세
 * 보유율이 고르지 않아(실측 en 90% · ja 42% · ko 14%) 화면이 분모를 함께 밝혀야 한다.
 */
export function useCardsetPriceSummary(cardsetSerialNo: number | null): CardsetPriceSummary {
  const query = useInfiniteQuery<CardSearchResponse, Error>({
    queryKey: listKey("cards", "set-all", { cardsetSerialNo }),
    queryFn: ({ pageParam }) =>
      searchCards({
        cardsetSerialNo: cardsetSerialNo as number,
        page: pageParam as number,
        size: CARDSET_ALL_PAGE_SIZE,
        sort: "NUMBER",
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.page + 1 : undefined),
    enabled: cardsetSerialNo !== null,
    staleTime: CATALOG_STALE_TIME,
  });

  // 다음 장이 있으면 스크롤을 기다리지 않고 이어 받는다 — 평균은 세트 **전체**의 값이라
  // 사용자가 목록을 끝까지 내리든 말든 다 받아야 완성된다.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return useMemo(() => {
    let sum = 0;
    let pricedCount = 0;
    let currency: string | null = null;
    for (const card of flattenCards(query.data)) {
      const price = card.listPrice;
      // 목록 셀의 판정과 같은 규칙 — 한쪽만 다르면 "시세 없음" 카드가 평균에 섞인다.
      if (price.display === "NONE" || price.amount === null) continue;
      sum += price.amount;
      pricedCount += 1;
      currency ??= price.currency;
    }
    return {
      average: pricedCount > 0 ? Math.round(sum / pricedCount) : null,
      currency,
      pricedCount,
      isComplete: cardsetSerialNo !== null && query.isSuccess && !query.hasNextPage,
    };
  }, [query.data, query.isSuccess, query.hasNextPage, cardsetSerialNo]);
}

/**
 * 카드 상세 — `cardSerialNo`(수집서버 전역 ID) 기준.
 *
 * URL 파라미터가 숫자로 파싱되지 않으면 호출하지 않는다. 더미 시절의 문자열 uid
 * (`card-sv4a-205-ko`)로 들어오는 링크가 아직 남아 있어, 그대로 넘기면 BE 가 400 을
 * 낸다 — 화면은 "카드를 찾을 수 없습니다"로 조용히 떨어지는 편이 낫다.
 */
/**
 * 시세 이력 (SC-007 히스토리 그래프 · 근거 값).
 *
 * ⚠ 기간이 키에 들어간다 — 7/30/90 을 각각 캐시해 스위치를 오갈 때 다시 안 받는다.
 * ⚠ 응답이 **null 일 수 있다**(시세 없는 카드). react-query 는 undefined 를 금지하므로
 *   fetcher 가 null 을 그대로 돌려주는 것이 맞다.
 */
export function useCardPriceHistoryQuery(
  cardSerialNo: number | null,
  days: HistoryPeriod,
  enabled = true,
) {
  return useQuery<PriceHistory | null>({
    queryKey: [...detailKey("cards", cardSerialNo ?? 0), "price-history", days],
    queryFn: () => fetchCardPriceHistory(cardSerialNo as number, days),
    enabled: enabled && cardSerialNo !== null,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCardDetailQuery(cardSerialNo: number | null) {
  return useQuery<CardDetail>({
    queryKey: detailKey("cards", cardSerialNo ?? 0),
    queryFn: () => fetchCardDetail(cardSerialNo as number),
    enabled: cardSerialNo !== null,
    staleTime: CATALOG_STALE_TIME,
  });
}
